import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Maximize2, Pause, Play, X, ExternalLink } from 'lucide-react';
import { BASE_URL } from '../constants';
import { getIntlLocale } from '../utils';

// «Поток событий» в разделе «Обзор». GET /api/dashboard/recent (BFF → IRIS, организация из JWT).
//
// Сцена: вокруг центра SOC кружат 10 последних алертов организации (цвет — критичность, крупные —
// High/Critical, ближе к центру — новее). Новый алерт прилетает из серого потока и встаёт на
// орбиту, самый старый гаснет. Серый поток — не телеметрия, а наглядная активность: его плотность
// задаёт число алертов за час (оно же подписано в легенде). Нет данных — поток гаснет.
// Клик по точке (или по строке ленты) останавливает её и открывает панель алерта с переходом в журнал.
//
// Опрос раз в 15 с и анимация — только пока блок на экране и вкладка браузера открыта.
// После ошибки опрос реже (30 → 60 с), при завершённой сессии и без IRIS — останавливается.
// Сессию завершённой считает и App (sessionEnded — по сроку или по 401 любого запроса):
// тогда сразу встают и опрос, и анимация, не дожидаясь собственного 401.

const REFRESH_MS = 15_000;
const ERROR_MIN_MS = 30_000;
const ERROR_MAX_MS = 60_000;
const FETCH_TIMEOUT_MS = 20_000;
const BIG = new Set(['High', 'Critical']);
// «Лавина»: за один опрос летят не больше FLY_PER_POLL самых новых, в очереди полёта — не больше
// MAX_QUEUE; остальные сразу встают на орбиту и сразу видны в ленте, чтобы лента не отставала
const FLY_PER_POLL = 3;
const MAX_QUEUE = 4;
const BURST_MIN = 5;          // столько новых за опрос — показываем бейдж «+N новых»
const BURST_SHOW_MS = 20_000;
// Заставка: при первом открытии обзора после загрузки страницы (или нового входа) прилетают
// алерты, пришедшие С ПРОШЛОГО ВИЗИТА (id больше запомненного), по очереди от старых к новым,
// не больше INTRO_FLY; остальные новые сразу стоят на орбите, а если новых больше INTRO_FLY —
// разовый бейдж «+N с прошлого визита». Прошлого визита нет (первый вход в этом браузере или
// хранилище недоступно) — прилетают три самых новых. Новых нет — ничего не летит.
// Флаг на уровне модуля: смена вкладки портала (компонент монтируется заново) заставку не
// повторяет, полная перезагрузка страницы — повторяет. Сбрасывают его конец сессии и выход.
const INTRO_FLY = 3;
const INTRO_MIN_SHOWN = 0.4;  // заставка ждёт, пока сцена видна на экране хотя бы на 40%
const SINCE_SHOW_MS = 30_000;
let introPlayed = false;
export function resetAlertStreamIntro() { introPlayed = false; }

// Последний виденный id — в localStorage, отдельно для каждого пользователя и организации.
// Хранится только число (id алерта), без содержимого. null — значения нет или хранилище недоступно
const SEEN_PREFIX = 'as_last_seen:';
function readLastSeen(key) {
  if (!key) return null;
  try {
    const n = Number(localStorage.getItem(SEEN_PREFIX + key));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}
function writeLastSeen(key, id) {
  if (!key || !(id > 0)) return;
  try {
    // только вперёд: если в IRIS удалили новые алерты, запомненное не откатываем
    const prev = readLastSeen(key);
    if (prev == null || id > prev) localStorage.setItem(SEEN_PREFIX + key, String(id));
  } catch {
    // хранилище недоступно или переполнено — в следующий раз прилетят три самых новых
  }
}
const STOP_ERRORS = new Set(['iris_not_configured', 'iris_customer_not_configured', 'iris_token_not_configured']);

// Те же цвета, что на диаграмме критичности в DashboardStats
const SEVERITY_COLORS = {
  Critical: '#e74c3c',
  High: '#e67e22',
  Medium: '#f1c40f',
  Low: '#3498db',
};
const SEVERITY_KEYS = ['Critical', 'High', 'Medium', 'Low', 'Informational', 'Unspecified'];
const sevColor = (s) => SEVERITY_COLORS[s] || '#6b7280';
const DOT_SIZE = { Critical: 6, High: 5, Medium: 3.8, Low: 3.4 };

// Сцена — «окно в космос»: тёмный фон, туманности, мерцающие звёзды
const FLOW_COLORS = ['#f87171', '#cbd5e1', '#64748b'];   // поток активности: красные, светлые, тусклые частицы
const FLOW_IDLE = 12;      // столько частиц, когда данных нет (загрузка, ошибка, нет IRIS)
const FLOW_IDLE_DIM = 0.35;

// Анимации появления алерта. Суть: алерт рождается ИЗ ПОТОКА — частица отрывается
// от серого потока слева, разгорается цветом критичности и летит на своё место на орбите.
//   glide — дуга к внешнему кругу, по нему к своему месту, мягкое снижение
//   drop  — поднимается из потока дугой и опускается на своё место сверху (или снизу)
//   chase — заходит прямо на своё кольцо и не спеша догоняет своё место по нему
// Каждый алерт получает вид случайно
const ARRIVALS = ['glide', 'drop', 'chase'];

// ─── Сцена на canvas (без React: 60 кадров в секунду через state были бы слишком дорогими) ───

function createScene({ stage, canvas, hub, labels, reduced, onArrive, onSelect, tipText }) {
  const ctx = canvas.getContext('2d');
  let w = 0, h = 0, cx = 0, cy = 0, R = 160, t = 0, last = 0, raf = 0, nextLaunch = 0;
  let active = false, paused = false;
  // Поток: целевая плотность и яркость и текущие (плавно догоняют цель — без скачков при смене)
  let flowN = FLOW_IDLE, flowDim = FLOW_IDLE_DIM, flowNf = 0, flowA = FLOW_IDLE_DIM;
  let flights = [], pulses = [], queue = [], sparks = [];
  // id → { alert, a0, speed, s, v, incoming, ignite, leaving, frozenA }
  //   s/v — масштаб точки и его скорость (пружина), ignite — когда прилетела (вспышка)
  const orbit = new Map();
  let rankOf = new Map();
  let selected = null, highlight = null, pressed = null, panel = null, hover = null;
  const TILT = -0.32, COS = Math.cos(TILT), SIN = Math.sin(TILT);   // наклон орбиты, как у колец

  let seed = 82457;
  const rand = () => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
  const stars = Array.from({ length: 160 }, () => ({ x: rand(), y: rand(), a: 0.08 + rand() * 0.35, z: rand() }));
  const flow = Array.from({ length: 300 }, () => ({ p: rand(), a: (rand() - 0.5) * 2, r: 0.4 + rand() * 1.2, s: rand() * 6.28, v: 0.03 + rand() * 0.035, z: rand() }));

  function resize() {
    const b = stage.getBoundingClientRect();
    w = b.width; h = b.height;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
    canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cx = w * (w < 640 ? 0.62 : 0.66); cy = h * 0.56;
    R = Math.min(w * 0.2, 200);
    hub.style.left = `${cx}px`; hub.style.top = `${cy}px`;
    renderBackdrop(dpr);
    if (selected != null) placePanel();
    draw();
  }
  const ro = new ResizeObserver(resize);

  const path = (p, a, ph) => ({
    x: -w * 0.04 + (cx + w * 0.04) * p,
    y: cy + a * h * 0.36 * Math.pow(1 - p, 0.8) + Math.sin(p * 5 + ph) * 18 * Math.sin(p * Math.PI),
  });
  const bez = (p, a, b, c, d) => { const v = 1 - p; return v * v * v * a + 3 * v * v * p * b + 3 * v * p * p * c + p * p * p * d; };
  function dot(x, y, r, color, a) { ctx.globalAlpha = a; ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
  // Ореол — готовая «наклейка» на каждый цвет: рисуется один раз, дальше только копируется
  // (новый радиальный градиент в каждом кадре на каждую планету — заметная нагрузка)
  const glowSprites = new Map();
  function glowSprite(color) {
    let sp = glowSprites.get(color);
    if (!sp) {
      sp = document.createElement('canvas');
      sp.width = sp.height = 64;
      const g2 = sp.getContext('2d'), g = g2.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, `${color}cc`); g.addColorStop(0.35, `${color}55`); g.addColorStop(1, `${color}00`);
      g2.fillStyle = g; g2.fillRect(0, 0, 64, 64);
      glowSprites.set(color, sp);
    }
    return sp;
  }
  function glowAt(x, y, r, color, a) {
    if (r < 0.5 || a <= 0) return;
    ctx.globalAlpha = Math.min(1, a);
    ctx.drawImage(glowSprite(color), x - r, y - r, r * 2, r * 2);
  }

  // Орбита — эллипс с наклоном, как кольца. k — во сколько раз дальше базового кольца
  function ellipse(a, k) {
    const ex = Math.cos(a) * R * k, ey = Math.sin(a) * 74 * k;
    return { x: cx + ex * COS - ey * SIN, y: cy + ex * SIN + ey * COS };
  }
  const angleOf = (o) => (o.frozenA !== undefined ? o.frozenA : o.a0 + t * o.speed);
  const radiusOf = (id) => 0.82 + (rankOf.get(id) ?? 9) * 0.055;   // новее — ближе к центру
  const orbitPos = (o) => ({ ...ellipse(angleOf(o), radiusOf(o.alert.id)), a: angleOf(o) });

  // Пружина масштаба точки: нажали — вдавилась, отпустили — отскок с перелётом и успокоение
  function springTarget(o) {
    const id = o.alert.id;
    if (id === pressed) return 0.62;
    if (id === selected) return 1.55;
    if (id === highlight || id === hover) return 1.4;
    return 1;
  }
  function stepSprings(dt) {
    for (const o of orbit.values()) {
      const target = springTarget(o);
      if (reduced) { o.s = target; o.v = 0; continue; }
      // жёсткость 220, затухание 14 — мягкий отклик с небольшим перелётом
      const acc = 220 * (target - o.s) - 14 * o.v;
      o.v += acc * dt; o.s += o.v * dt;
    }
  }

  // Панель алерта открывается сбоку от выбранной точки — там, где больше места
  // Где поставить карточку размером bw×bh возле точки p: в сторону от центра, внутри сцены
  // и так, чтобы она не задевала круг SOC (радиус 58 + запас)
  const HUB_CLEAR = 74;
  function placeNear(p, bw, bh) {
    // Перебираем места по сетке и берём ближайшее к точке, которое не задевает SOC,
    // не закрывает саму точку и не вылезает за сцену. Считается только при прилёте и выборе
    let best = null, bestD = Infinity;
    for (let l = 8; l <= w - bw - 8; l += 10) {
      for (let tp = 8; tp <= h - bh - 8; tp += 8) {
        const hx = Math.max(l, Math.min(cx, l + bw)), hy = Math.max(tp, Math.min(cy, tp + bh));
        if ((hx - cx) ** 2 + (hy - cy) ** 2 <= HUB_CLEAR * HUB_CLEAR) continue;
        const px = Math.max(l, Math.min(p.x, l + bw)), py = Math.max(tp, Math.min(p.y, tp + bh));
        const d = Math.hypot(px - p.x, py - p.y);
        if (d < 14) continue;                       // карточка не должна закрывать саму точку
        if (d < bestD) { bestD = d; best = { left: l, top: tp }; }
      }
    }
    // Сцена слишком мала (телефон) — прижимаем к краю подальше от центра
    return best || { left: p.x >= cx ? Math.max(8, w - bw - 8) : 8, top: p.y >= cy ? 8 : Math.max(8, h - bh - 8) };
  }

  function placePanel() {
    const o = orbit.get(selected);
    if (!o) return;
    const p = orbitPos(o);
    const { left, top } = placeNear(p, 280, 170);
    const side = left >= p.x ? 'right' : 'left';
    panel = { x: p.x, y: p.y, left, top, side };
    onSelect(selected, { left, top, side, ox: p.x - left, oy: p.y - top });
  }

  // Фон — градиент и туманности — рисуется один раз в отдельный буфер (при изменении размера),
  // в каждом кадре он только копируется: большие градиенты в каждом кадре и тормозили страницу
  const backdrop = document.createElement('canvas');
  function renderBackdrop(dpr) {
    backdrop.width = canvas.width; backdrop.height = canvas.height;
    const b = backdrop.getContext('2d');
    b.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = b.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#0b1022'); g.addColorStop(0.6, '#111a33'); g.addColorStop(1, '#1a1030');
    b.fillStyle = g; b.fillRect(0, 0, w, h);
    const m = Math.max(w, h);
    for (const [x, y, r, color, a] of [[w * 0.25, h * 0.3, m * 0.45, '#4f46e5', 0.22], [cx, cy + 20, m * 0.38, '#be123c', 0.20],
      [w * 0.9, h * 0.85, m * 0.3, '#0e7490', 0.18]]) {
      const n = b.createRadialGradient(x, y, 0, x, y, r);
      n.addColorStop(0, color); n.addColorStop(1, `${color}00`);
      b.globalAlpha = a; b.fillStyle = n; b.fillRect(x - r, y - r, r * 2, r * 2);
    }
  }

  function drawBackground() {
    ctx.globalAlpha = 1;
    ctx.drawImage(backdrop, 0, 0, w, h);
    // звёзды мерцают; квадратики вместо кругов — в разы дешевле
    ctx.fillStyle = '#ffffff';
    for (const s of stars) {
      const tw = 0.55 + 0.45 * Math.sin(t * (1 + s.z * 2) + s.x * 40);
      const size = s.z > 0.92 ? 2 : 1.2;
      ctx.globalAlpha = Math.min(1, s.a * 1.8 * tw);
      ctx.fillRect((s.x * w + t * (0.15 + s.z * 0.3)) % w, s.y * h, size, size);
    }
  }

  function draw() {
    if (!w) return;
    ctx.clearRect(0, 0, w, h);
    drawBackground();
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(TILT);
    for (let i = 0; i < 3; i++) {
      ctx.globalAlpha = 0.35 - i * 0.08; ctx.strokeStyle = '#fca5a5'; ctx.lineWidth = 1.1 - i * 0.25;
      ctx.beginPath(); ctx.ellipse(0, 0, R * (0.82 + i * 0.25), 74 * (0.82 + i * 0.25), 0, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
    const flowCount = Math.ceil(flowNf);
    for (let i = 0; i < flowCount; i++) {
      const s = flow[i];
      const p = (s.p + t * s.v) % 1, pos = path(p, s.a, s.s);
      // последняя частица проявляется/гаснет по дробной части — плотность меняется плавно
      const a = (0.45 + s.z * 0.55) * Math.min(1, (1 - p) * 6) * Math.min(1, p * 6) * flowA * Math.min(1, flowNf - i);
      const size = s.r * (1.4 + p);
      ctx.globalAlpha = a; ctx.fillStyle = s.z > 0.82 ? FLOW_COLORS[0] : s.z > 0.45 ? FLOW_COLORS[1] : FLOW_COLORS[2];
      ctx.fillRect(pos.x - size / 2, pos.y - size / 2, size, size);
    }

    // Волны от точки: при посадке и при выборе
    for (const p of pulses) {
      const life = (t - p.born) / p.dur;
      if (life < 0 || life > 1) continue;
      const e = 1 - Math.pow(1 - life, 3);
      ctx.globalAlpha = (1 - life) * p.alpha; ctx.strokeStyle = p.color; ctx.lineWidth = p.width;
      ctx.beginPath(); ctx.ellipse(p.x, p.y, p.r0 + e * p.rx, p.r0 + e * p.ry, -0.3, 0, Math.PI * 2); ctx.stroke();
    }

    // Связь выбранной точки с панелью
    if (selected != null && panel) {
      const o = orbit.get(selected);
      if (o) {
        const p = orbitPos(o), color = sevColor(o.alert.severity);
        const ex = Math.max(panel.left, Math.min(p.x, panel.left + 280)), ey = Math.max(panel.top + 18, Math.min(p.y, panel.top + 150));
        ctx.globalAlpha = 0.55; ctx.strokeStyle = color; ctx.lineWidth = 1.2; ctx.setLineDash([3, 4]);
        ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(ex, ey); ctx.stroke(); ctx.setLineDash([]);
      }
    }

    // Алерты на орбите
    for (const o of orbit.values()) {
      if (o.incoming) continue;
      const { x, y } = orbitPos(o);
      const id = o.alert.id, sev = o.alert.severity, color = sevColor(sev);
      const rank = rankOf.get(id) ?? 9;
      const fade = o.leaving !== undefined ? Math.min(1, Math.max(0, 1 - (t - o.leaving) / 0.5)) : 1;
      const focus = selected != null && selected !== id ? 0.35 : 1;     // выбрана другая — эта приглушена
      const alpha = (1 - rank * 0.05) * fade * focus;
      const size = (DOT_SIZE[sev] || 3) * Math.max(0.3, o.s);
      // Вспышка при прилёте: яркий ореол гаснет за 2,5 с
      const ign = o.ignite !== undefined ? Math.max(0, 1 - (t - o.ignite) / 3.2) : 0;
      if (ign > 0) glowAt(x, y, size * (5 + 6 * ign), color, ign * 0.9 * fade);
      if (BIG.has(sev) || id === selected) glowAt(x, y, size * 3.2, color, alpha * 0.8);
      dot(x, y, size, color, alpha);
      dot(x, y, size * 0.38, '#ffffff', alpha * (0.85 + ign * 0.15));
      // тихий «пинг» по очереди у каждой планеты — видно, что на них можно нажать
      if (!reduced && selected == null && o.leaving === undefined) {
        const ph = (t * 0.22 + rank * 0.137) % 1;
        if (ph < 0.3) {
          const q = ph / 0.3;
          ctx.globalAlpha = (1 - q) * 0.45 * alpha; ctx.strokeStyle = color; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(x, y, size + 2 + q * 11, 0, Math.PI * 2); ctx.stroke();
        }
      }
      if ((id === highlight || id === hover) && id !== selected) {
        ctx.globalAlpha = fade; ctx.strokeStyle = color; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(x, y, size + 5, 0, Math.PI * 2); ctx.stroke();
      }
      if (id === selected) {
        // «Прицел»: вращающиеся уголки и мягко дышащее кольцо — сразу видно, какая точка выбрана
        const rr = size + 9 + (reduced ? 0 : Math.sin(t * 3) * 1.5);
        ctx.globalAlpha = 0.9; ctx.strokeStyle = color; ctx.lineWidth = 1.8;
        for (let i = 0; i < 4; i++) {
          const a = (reduced ? 0 : t * 0.9) + i * Math.PI / 2;
          ctx.beginPath(); ctx.arc(x, y, rr, a - 0.35, a + 0.35); ctx.stroke();
        }
        ctx.globalAlpha = 0.25; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(x, y, rr + 6, 0, Math.PI * 2); ctx.stroke();
      }
    }

    // Искры от комет
    for (const s of sparks) {
      const life = (t - s.born) / s.life;
      dot(s.x, s.y, s.r * (1 - life * 0.6), life < 0.35 ? '#fde68a' : s.color, (1 - life) * 0.85);
    }

    // Кометы: огненный хвост от раскалённой головы к цвету критичности
    for (const f of flights) {
      const tr = f.trail;
      // первые 0,9 с — серая частица потока «разгорается» в комету
      const lit = Math.min(1, (t - f.born) / 0.9);
      for (let j = 1; j < tr.length; j++) {
        const k = j / tr.length;                    // 0 — конец хвоста, 1 — голова
        ctx.globalAlpha = k * k * (f.big ? 0.95 : 0.7) * lit;
        ctx.strokeStyle = k > 0.85 ? '#fff7ed' : k > 0.6 ? '#fbbf24' : f.color;
        ctx.lineWidth = (f.big ? 5 : 3) * k + 0.4;
        ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(tr[j - 1].x, tr[j - 1].y); ctx.lineTo(tr[j].x, tr[j].y); ctx.stroke();
      }
      const head = tr[tr.length - 1];
      if (head) {
        if (lit < 1) dot(head.x, head.y, 1.8, FLOW_COLORS[1], 1 - lit);
        glowAt(head.x, head.y, (f.big ? 26 : 16) * lit, '#f59e0b', 0.8 * lit);
        glowAt(head.x, head.y, (f.big ? 14 : 9) * lit, f.color, 0.9 * lit);
        dot(head.x, head.y, (f.big ? 3.4 : 2.4) * (0.5 + 0.5 * lit), '#ffffff', lit);
      }
    }
    ctx.globalAlpha = 1; ctx.lineCap = 'butt';
    updateTip();
  }

  // Подсказка при наведении на планету: критичность и заголовок; следует за планетой
  const tip = document.createElement('div');
  tip.className = 'as-fcard as-tip';
  const tipSev = document.createElement('span'); tipSev.className = 'as-fcard-sev';
  const tipTitle = document.createElement('span'); tipTitle.className = 'as-fcard-title';
  tip.append(tipSev, tipTitle);
  labels.appendChild(tip);
  let tipFor = null, tipW = 200, tipH = 28;
  function updateTip() {
    const o = hover == null || hover === selected ? null : orbit.get(hover);
    if (!o || o.incoming || o.leaving !== undefined) {
      if (tipFor !== null) { tip.classList.remove('on'); tipFor = null; }
      return;
    }
    if (tipFor !== hover) {
      tipFor = hover;
      tip.style.setProperty('--sev', sevColor(o.alert.severity));
      tipSev.textContent = tipText(o.alert).severity;
      tipTitle.textContent = o.alert.title || '—';
      tip.classList.add('on');
      tipW = tip.offsetWidth || 200; tipH = tip.offsetHeight || 28;   // один раз на новое содержимое
    }
    const p = orbitPos(o), tw = tipW, th = tipH;
    // справа-сверху от планеты; у края — с другой стороны
    let x = p.x + 14, y = p.y - th - 12;
    if (x + tw > w - 8) x = p.x - 14 - tw;
    if (y < 8) y = p.y + 14;
    tip.style.transform = `translate(${x}px,${y}px)`;
  }

  function addToOrbit(alert, incoming) {
    if (orbit.has(alert.id)) return orbit.get(alert.id);
    // Свободное место — подальше от соседей, чтобы точки не слипались
    const taken = [...orbit.values()].map(o => ((angleOf(o) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2));
    let best = rand() * Math.PI * 2, bestGap = -1;
    for (let i = 0; i < 24; i++) {
      const cand = (i / 24) * Math.PI * 2;
      const gap = taken.length ? Math.min(...taken.map(a => Math.min(Math.abs(a - cand), Math.PI * 2 - Math.abs(a - cand)))) : 1;
      if (gap > bestGap) { bestGap = gap; best = cand; }
    }
    const o = { alert, a0: best - t * 0.06, speed: 0.06, s: 1, v: 0, incoming };
    orbit.set(alert.id, o);
    return o;
  }

  // Полёт идёт ВНУТРИ серого потока: первая половина пути — ровно по его линии,
  // потом плавный переход на кольцо и по нему к своему месту. За пределы потока комета выходит
  // только у самой орбиты
  const FLOW_PART = 0.5;      // доля пути по линии потока
  const BRIDGE_PART = 0.15;   // доля пути на плавный переход с потока на кольцо
  const JOIN_GLIDE = 1.45;    // glide входит на кольцо чуть шире своей орбиты и снижается в конце
  // Точка сцены → координаты орбиты: угол и «радиус» в долях базового кольца
  function toOrbit(x, y) {
    const dx = x - cx, dy = y - cy;
    const ex = (dx * COS + dy * SIN) / R, ey = (-dx * SIN + dy * COS) / 74;
    return { a: Math.atan2(ey, ex), k: Math.hypot(ex, ey) };
  }
  const unit = (x, y) => { const l = Math.hypot(x, y) || 1; return { x: x / l, y: y / l }; };
  // Угол от a до b в выбранную сторону; небольшой «перелёт» назад не превращается в полный виток
  function sweep(a, b, dir) {
    let d = ((b - a) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    if (dir < 0) d -= Math.PI * 2;
    if (Math.abs(d) > Math.PI * 1.6) d -= dir * Math.PI * 2;
    return d;
  }
  const smooth = (q) => q * q * (3 - 2 * q);

  function flightPos(f, u) {
    if (u < FLOW_PART) return path(f.p0 + (f.p1 - f.p0) * (u / FLOW_PART), f.fanA, f.ph);
    const o = orbit.get(f.alert.id);
    const tgtA = o ? angleOf(o) : f.aE;
    const kT = radiusOf(f.alert.id);
    const E = f.E, tf = f.tf;
    if (f.kind === 'drop') {
      // с конца линии потока — мягкой дугой на своё место, подходя сверху (или снизу)
      const q = (u - FLOW_PART) / (1 - FLOW_PART), tp = ellipse(tgtA, kT);
      return { x: bez(q, E.x, E.x + tf.x * 60, tp.x, tp.x), y: bez(q, E.y, E.y + tf.y * 60, tp.y - f.dropDir * 70, tp.y) };
    }
    const joinK = f.kind === 'chase' ? kT : JOIN_GLIDE;
    const aR = f.aE + f.dir * f.dA;                     // где мост выходит на кольцо
    if (u < FLOW_PART + BRIDGE_PART) {
      const q = (u - FLOW_PART) / BRIDGE_PART, R1 = ellipse(aR, joinK), R2 = ellipse(aR + f.dir * 0.05, joinK);
      const rt = unit(R2.x - R1.x, R2.y - R1.y);
      // направляющие не длиннее трети моста — иначе на коротком мосту кривая делает петлю
      const L = Math.min(40, Math.hypot(R1.x - E.x, R1.y - E.y) / 3);
      return { x: bez(q, E.x, E.x + tf.x * L, R1.x - rt.x * L, R1.x), y: bez(q, E.y, E.y + tf.y * L, R1.y - rt.y * L, R1.y) };
    }
    const q = (u - FLOW_PART - BRIDGE_PART) / (1 - FLOW_PART - BRIDGE_PART);
    const a = aR + sweep(aR, tgtA, f.dir) * q;
    return ellipse(a, joinK + (kT - joinK) * smooth(q));
  }

  // Мягкий разгон и мягкое торможение для всех видов — без рывков
  const ease = (p) => p * p * p * (p * (p * 6 - 15) + 10);

  function makeCard(f, texts) {
    const el = document.createElement('div');
    el.className = 'as-fcard';
    el.style.setProperty('--sev', f.color);
    const sev = document.createElement('span'); sev.className = 'as-fcard-sev'; sev.textContent = texts.severity;
    const title = document.createElement('span'); title.className = 'as-fcard-title'; title.textContent = f.alert.title || '—';
    el.append(sev, title);
    labels.appendChild(el);
    const st = path(f.p0, f.fanA, f.ph);
    return { el, w: el.offsetWidth || 200, h: el.offsetHeight || 30, x: st.x, y: st.y };
  }

  // Карточка летит следом за головой кометы, с той стороны, что дальше от центра, и с запаздыванием
  function followCard(card, head, dt) {
    const dx = head.x - cx, dy = head.y - cy, d = Math.hypot(dx, dy) || 1;
    let tx = head.x + (dx / d) * 22 - (dx < 0 ? card.w : 0);
    let ty = head.y + (dy / d) * 16 - (dy < 0 ? card.h : 0);
    tx = Math.max(8, Math.min(w - card.w - 8, tx)); ty = Math.max(8, Math.min(h - card.h - 8, ty));
    const k = Math.min(1, dt * 3.2);
    card.x += (tx - card.x) * k; card.y += (ty - card.y) * k;
    card.el.style.transform = `translate(${card.x}px,${card.y}px)`;
  }

  function launch({ alert, texts }) {
    const o = addToOrbit(alert, true);
    const color = sevColor(alert.severity), big = BIG.has(alert.severity);
    const kind = ARRIVALS[Math.floor(rand() * ARRIVALS.length)];
    const kT = radiusOf(alert.id), tgtA = angleOf(o);
    // место посадки: для drop от него зависит, из какой части потока стартовать
    const tp = ellipse(tgtA, kT), dropDir = tp.y < cy ? 1 : -1;
    // линия потока: та же, по которым текут серые частицы; для drop — с той стороны, откуда он опускается
    const p0 = 0.18 + rand() * 0.12;
    const fanA = kind === 'drop' ? -dropDir * (0.35 + rand() * 0.35) : (rand() - 0.5) * 1.4;
    const ph = rand() * 6.28;
    // где линия потока подходит к кольцу входа — там комета сходит с потока
    const joinK = kind === 'chase' ? kT : kind === 'drop' ? Math.max(kT, 1.2) + 0.35 : JOIN_GLIDE;
    let p1 = 0.9;
    for (let q = p0 + 0.05; q < 0.95; q += 0.01) {
      const pt = path(q, fanA, ph);
      if (toOrbit(pt.x, pt.y).k <= joinK) { p1 = q; break; }
    }
    const E = path(p1, fanA, ph), E2 = path(p1 + 0.01, fanA, ph);
    const tf = unit(E2.x - E.x, E2.y - E.y);
    const aE = toOrbit(E.x, E.y).a;
    const fwd = ((tgtA - aE) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    const dir = fwd <= Math.PI ? 1 : -1;
    const dA = Math.min(0.45, Math.abs(sweep(aE, tgtA, dir)) * 0.4);
    const dur = kind === 'drop' ? 6.4 + rand() * 1.2 : kind === 'chase' ? 7.8 + rand() * 1.6 : 7.2 + rand() * 1.4;
    const f = { alert, color, big, kind, born: t, dur, trail: [], p0, p1, fanA, ph, E, tf, aE, dir, dA, dropDir, x0: E.x, y0: E.y };
    f.card = makeCard(f, texts);
    flights.push(f);
  }

  function arrive(f) {
    const o = orbit.get(f.alert.id);
    if (o) {
      o.incoming = false; o.ignite = t;
      o.s = 1.6; o.v = 0;            // чуть крупнее и мягко садится на орбиту
      const p = orbitPos(o);
      pulses.push({ born: t, dur: 1.8, x: p.x, y: p.y, r0: 4, rx: 40, ry: 40, color: f.color, width: 1.6, alpha: 0.6 });
      for (let i = 0; i < (f.big ? 16 : 8); i++) {
        const a = rand() * Math.PI * 2, sp = 15 + rand() * 45;
        sparks.push({ x: p.x, y: p.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 1 + rand() * 1.6, born: t, life: 0.5 + rand() * 0.5, color: f.color });
      }
    }
    f.card?.el.remove();   // на орбите карточки уже нет — только планета
    onArrive(f.alert, f.big, null);
  }

  // 30 кадров в секунду, пока что-то летит или пользователь взаимодействует; в покое — 20:
  // фон, орбита и поток движутся медленно, разницы на глаз нет, а работы на треть меньше
  const busy = () => flights.length || queue.length || sparks.length || pulses.length || hover != null || pressed != null || selected != null;
  function frame(now) {
    raf = 0;
    if (!active || paused || document.hidden) { last = 0; return; }
    if (last && now - last < (busy() ? 1000 / 30 : 1000 / 20) - 2) { raf = requestAnimationFrame(frame); return; }
    const dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
    last = now;
    t += dt;
    const k = Math.min(1, dt * 1.2);
    flowNf += (flowN - flowNf) * k; flowA += (flowDim - flowA) * k;
    if (queue.length && t >= nextLaunch && flights.length < 6) {
      launch(queue.shift());
      nextLaunch = t + (queue.length > 3 ? 1.4 : 2.6) + rand() * 1.0;
    }
    for (const f of flights) {
      const p = Math.min(1, (t - f.born) / f.dur);
      const head = flightPos(f, ease(p));
      f.trail.push(head);
      if (f.trail.length > (f.big ? 48 : 32)) f.trail.shift();   // полёт медленнее — хвост длиннее в кадрах
      // мини-карточка летит следом и гаснет к посадке
      if (f.card) {
        followCard(f.card, head, dt);
        f.card.el.style.opacity = p > 0.75 ? String(Math.max(0, (1 - p) / 0.25)) : '';
      }
      // искры отрываются от головы и тлеют
      if (rand() < (f.big ? 0.6 : 0.3)) {
        const a = rand() * Math.PI * 2, sp = 10 + rand() * 30;
        sparks.push({ x: head.x, y: head.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 0.6 + rand() * 1.2, born: t, life: 0.4 + rand() * 0.5, color: f.color });
      }
    }
    for (const f of flights.filter(f => t - f.born >= f.dur)) arrive(f);
    flights = flights.filter(f => t - f.born < f.dur);
    for (const s of sparks) { s.x += s.vx * dt; s.y += s.vy * dt; s.vx *= 0.94; s.vy = s.vy * 0.94 + 20 * dt; }
    sparks = sparks.filter(s => t - s.born < s.life).slice(-260);
    pulses = pulses.filter(p => t - p.born < p.dur);
    for (const [id, o] of orbit) if (o.leaving !== undefined && t - o.leaving > 0.5) orbit.delete(id);
    stepSprings(dt);
    draw();
    raf = requestAnimationFrame(frame);
  }
  const run = () => {
    if (reduced) { stepSprings(0); draw(); return; }
    if (!raf && active && !paused) raf = requestAnimationFrame(frame);
  };
  const onVisibility = () => run();
  document.addEventListener('visibilitychange', onVisibility);

  function hitTest(x, y) {
    let best = null, bestD = 16 * 16;
    for (const o of orbit.values()) {
      if (o.leaving !== undefined || o.incoming) continue;
      const p = orbitPos(o), d = (p.x - x) ** 2 + (p.y - y) ** 2;
      if (d < bestD) { bestD = d; best = o.alert.id; }
    }
    return best;
  }
  const local = (e) => { const b = canvas.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
  const onMove = (e) => {
    const id = hitTest(...local(e));
    canvas.style.cursor = id !== null ? 'pointer' : '';
    if (id !== hover) { hover = id; run(); }
  };
  // Нажатие с «давлением»: пока кнопка зажата, точка вдавлена; отпустили на ней же — выбор
  const onDown = (e) => { pressed = hitTest(...local(e)); if (pressed !== null) { e.preventDefault(); run(); } };
  const onUp = (e) => {
    const id = hitTest(...local(e)), was = pressed;
    pressed = null;
    if (was !== null && id === was) api.select(id === selected ? null : id);
    else if (was === null && id === null) api.select(null);
    run();
  };
  const onLeave = () => { pressed = null; hover = null; canvas.style.cursor = ''; run(); };
  canvas.addEventListener('mousemove', onMove);
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointerleave', onLeave);

  ro.observe(stage);   // в самом конце: первый resize рисует сцену, всё должно быть объявлено

  const api = {
    setActive(on) { active = on; if (on) { resize(); run(); } },
    setPaused(p) { paused = p; run(); },
    // count1h — алертов за час; null — данных нет (загрузка, ошибка, нет IRIS): поток редкий и тусклый
    setActivity(count1h) {
      flowN = count1h == null ? FLOW_IDLE : count1h === 0 ? 30 : count1h <= 5 ? 70 : count1h <= 30 ? 140 : count1h <= 120 ? 220 : 300;
      flowDim = count1h == null ? FLOW_IDLE_DIM : 1;
      if (reduced) { flowNf = flowN; flowA = flowDim; draw(); }
    },
    syncOrbit(items, pendingIds, animate) {
      rankOf = new Map(items.map((a, i) => [a.id, i]));
      const keep = new Set(items.map(a => a.id));
      for (const [id, o] of orbit) {
        if (!keep.has(id) && o.leaving === undefined) {
          o.leaving = t;
          if (id === selected) api.select(null);
          if (reduced || !animate) orbit.delete(id);
        }
      }
      items.forEach(a => { if (!pendingIds.has(a.id)) addToOrbit(a, false); });
      if (reduced) draw();
    },
    // items: [{ alert, texts }] от старых к новым; texts — подписи карточки на языке интерфейса
    enqueue(items) {
      if (reduced) { items.forEach(i => { addToOrbit(i.alert, false); onArrive(i.alert, BIG.has(i.alert.severity), null); }); draw(); return; }
      queue.push(...items);
      // очередь не растёт бесконечно: самые старые из ожидающих встают на орбиту без полёта
      while (queue.length > MAX_QUEUE) {
        const it = queue.shift();
        addToOrbit(it.alert, false);
        onArrive(it.alert, BIG.has(it.alert.severity), null);
      }
      run();
    },
    // Выбор точки: она замирает (остальные кружат), приглушает соседей и открывает панель
    select(id) {
      const prev = selected == null ? null : orbit.get(selected);
      if (prev && prev.frozenA !== undefined) { prev.a0 = prev.frozenA - t * prev.speed; delete prev.frozenA; }
      const o = id == null ? null : orbit.get(id);
      if (!o || o.leaving !== undefined || o.incoming) {
        selected = null; panel = null;
        onSelect(null, null);
      } else {
        o.frozenA = angleOf(o);
        selected = id;
        o.v -= 6;                       // короткий «толчок» вниз перед отскоком, как от нажатия
        const p = orbitPos(o);
        pulses.push({ born: t, dur: 0.7, x: p.x, y: p.y, r0: 6, rx: 30, ry: 30, color: sevColor(o.alert.severity), width: 1.6, alpha: 0.7 });
        placePanel();
      }
      run();
    },
    highlight(id) { highlight = id; run(); },
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
      flights.forEach(f => f.card?.el.remove());
      tip.remove();
    },
  };
  return api;
}

// ─── Компонент ────────────────────────────────────────────────────────────────

// seenKey — «пользователь:организация» для запоминания последнего виденного алерта (null — не запоминаем)
export function AlertStream({ total24h = null, onOpenAlert, sessionEnded = false, seenKey = null }) {
  const { t, i18n } = useTranslation();
  const locale = getIntlLocale(i18n.language);
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const hubRef = useRef(null);
  const labelsRef = useRef(null);
  const sceneRef = useRef(null);
  const prevMaxRef = useRef(null);   // самый большой id, что уже видели (null — первой загрузки ещё не было)
  const pendingRef = useRef(new Set());
  const pausedRef = useRef(false);
  const introWaitRef = useRef(null);       // заставка, ждущая показа сцены: { items (от старых к новым), since }
  const introIdsRef = useRef(new Set());   // id, летящие в заставке: их прилёт — не «новый алерт»
  const onscreenRef = useRef(false);       // сцена реально на экране (не только в запасе 100px)
  const reduced = useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches).current;

  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState(() => new Set());   // id в полёте — в ленте появятся по прилёту
  const [arrived, setArrived] = useState(() => new Set());   // id, прилетевшие на глазах, — подсветка в ленте
  const [state, setState] = useState('loading');             // loading | ok | error | nodata | ended
  const [count1h, setCount1h] = useState(null);
  const [burstN, setBurstN] = useState(0);              // «лавина»: сколько новых пришло за последний опрос
  const prevCountRef = useRef(null);
  const burstTimer = useRef(0);
  const [since, setSince] = useState(null);             // бейдж «+N с прошлого визита»: { n, many } или null
  const sinceTimer = useRef(0);
  useEffect(() => () => { clearTimeout(burstTimer.current); clearTimeout(sinceTimer.current); }, []);
  const [selected, setSelected] = useState(null);             // { id, x, y, w, h }
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [announce, setAnnounce] = useState('');
  const time = useCallback((iso) => (iso
    ? new Date(iso).toLocaleTimeString(locale, { timeZone: 'Asia/Almaty', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—'), [locale]);
  const sevLabel = useCallback((name) => (SEVERITY_KEYS.includes(name)
    ? t(`AlertStream.sev_${name.toLowerCase()}`)
    : (name || t('AlertStream.sev_unspecified'))), [t]);
  const cardTexts = useCallback((a) => ({ severity: sevLabel(a.severity).toUpperCase() }), [sevLabel]);

  // Прилёт алерта в центр: показать в ленте, вспышка центра и карточка для крупных
  // Алерты заставки не новые — подсветка в ленте есть, а объявления для экранного диктора нет
  const onArrive = useCallback((a, big) => {
    pendingRef.current.delete(a.id);
    setPending(new Set(pendingRef.current));
    setArrived(s => new Set(s).add(a.id));
    setTimeout(() => setArrived(s => { const n = new Set(s); n.delete(a.id); return n; }), 3000);
    const intro = introIdsRef.current.delete(a.id);
    if (big && !intro) {
      setAnnounce(t('AlertStream.announce', { severity: sevLabel(a.severity), title: a.title, source: a.source }));
    }
  }, [t, sevLabel]);
  const onSelect = useCallback((id, pos) => {
    setOpenError(false);
    setSelected(id == null ? null : { id, ...pos });
  }, []);
  const handlers = useRef({});
  handlers.current = { onArrive, onSelect, cardTexts };

  const showSince = useCallback((s) => {
    if (!s) return;
    setSince(s);
    clearTimeout(sinceTimer.current);
    sinceTimer.current = setTimeout(() => setSince(null), SINCE_SHOW_MS);
  }, []);
  // Запуск заставки — только когда сцена реально на экране, не на паузе и сессия жива;
  // иначе ждёт (её вызывают ещё раз: появление сцены на экране и снятие паузы)
  const playIntro = useCallback(() => {
    const wait = introWaitRef.current, scene = sceneRef.current;
    if (!wait || !scene || !onscreenRef.current || pausedRef.current || endedRef.current) return;
    introWaitRef.current = null;
    introPlayed = true;
    wait.items.forEach(a => introIdsRef.current.add(a.id));
    // интервал между стартами задаёт сама сцена (nextLaunch: 2,6–3,6 с) — кометы идут друг за другом
    scene.enqueue(wait.items.map(a => ({ alert: a, texts: handlers.current.cardTexts(a) })));
    showSince(wait.since);
  }, [showSince]);

  useEffect(() => {
    const scene = createScene({
      stage: stageRef.current, canvas: canvasRef.current, hub: hubRef.current, labels: labelsRef.current, reduced,
      onArrive: (...args) => handlers.current.onArrive(...args),
      onSelect: (...args) => handlers.current.onSelect(...args),
      tipText: (a) => handlers.current.cardTexts(a),
    });
    sceneRef.current = scene;
    return () => scene.destroy();
  }, [reduced]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: '100px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // Отдельно — видна ли сама сцена на экране: опрос стартует уже за 100px до экрана,
  // а заставка не должна пролететь там, где её не видно
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => {
      onscreenRef.current = e.intersectionRatio >= INTRO_MIN_SHOWN;
      if (onscreenRef.current) playIntro();
    }, { threshold: [0, INTRO_MIN_SHOWN] });
    observer.observe(el);
    return () => observer.disconnect();
  }, [playIntro]);

  // Сессия завершена — сцена замирает на последнем кадре, ответ уже отправленного запроса не применяем
  const endedRef = useRef(sessionEnded);
  useEffect(() => {
    endedRef.current = sessionEnded;
    if (sessionEnded) {
      setState('ended');
      // следующий вход в этой же вкладке снова покажет заставку (ждущую заставку не запустит playIntro)
      introPlayed = false;
    }
  }, [sessionEnded]);
  useEffect(() => { sceneRef.current?.setActive(visible && !sessionEnded); }, [visible, sessionEnded]);
  useEffect(() => {
    pausedRef.current = paused;
    sceneRef.current?.setPaused(paused);
    if (!paused) playIntro();
  }, [paused, playIntro]);
  // Число за час — только пока данные свежие: при ошибке, без IRIS и после конца сессии его нет,
  // поток гаснет, в легенде «—» (прежнее число выглядело бы как живая активность)
  const activity = state === 'ok' ? count1h : null;
  useEffect(() => { sceneRef.current?.setActivity(activity); }, [activity]);
  // Esc закрывает панель алерта
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') sceneRef.current?.select(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // Один запрос ленты. Возвращает, через сколько мс спрашивать снова (null — больше не спрашивать)
  const errorDelay = useRef(ERROR_MIN_MS);
  const load = useCallback(async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(`${BASE_URL}/api/dashboard/recent`, { credentials: 'include', signal: ctrl.signal });
      const json = await res.json().catch(() => ({}));
      if (endedRef.current) return null;
      if (res.status === 401) { setState('ended'); return null; }       // сессия завершена — окно входа покажет App
      if (res.status === 503 && STOP_ERRORS.has(json.error)) { setState('nodata'); return null; }
      if (!res.ok) throw new Error(String(res.status));
      const alerts = Array.isArray(json.alerts) ? json.alerts : [];
      const scene = sceneRef.current;
      let fly = [];
      const loaded = prevMaxRef.current !== null;
      if (loaded) {
        // Новые — только с id больше прежнего: если в IRIS удалили алерт, в 10 последних поднимается
        // старый — он встаёт на орбиту тихо, без полёта и без «+N новых».
        // Новые летят по сцене (от старых к новым); много разом — сразу на орбиту
        const added = alerts.filter(a => a.id > prevMaxRef.current).reverse();
        if (!pausedRef.current) fly = added.slice(-FLY_PER_POLL);
        // сколько пришло на самом деле: в ленте только 10, поэтому берём и прирост счётчика за час
        const grew = typeof json.count_1h === 'number' && typeof prevCountRef.current === 'number'
          ? json.count_1h - prevCountRef.current : 0;
        const burst = Math.max(added.length, grew);
        if (burst >= BURST_MIN) {
          setBurstN(burst);
          clearTimeout(burstTimer.current);
          burstTimer.current = setTimeout(() => setBurstN(0), BURST_SHOW_MS);
        }
      }
      if (typeof json.count_1h === 'number') prevCountRef.current = json.count_1h;
      // Первая загрузка: планеты сразу стоят на орбите, кроме заставки (один раз за загрузку страницы)
      let intro = [];
      if (!loaded && !introPlayed) {
        const seen = readLastSeen(seenKey);
        const fresh = seen == null ? alerts.slice(0, INTRO_FLY) : alerts.filter(a => a.id > seen);
        // в ленте только 10 последних: если новые все 10, на самом деле их может быть и больше
        const sinceInfo = seen != null && fresh.length > INTRO_FLY
          ? { n: fresh.length, many: fresh.length === alerts.length && alerts.length >= 10 } : null;
        if (reduced || !fresh.length) {
          // без анимации (или нечего показывать) — заставка на этом и закончена, бейдж — сразу
          introPlayed = true;
          showSince(sinceInfo);
        } else {
          intro = fresh.slice(0, INTRO_FLY).reverse();   // от старых к новым; остальные новые — сразу на орбиту
        }
        if (intro.length) introWaitRef.current = { items: intro, since: sinceInfo, at: Date.now() };
      } else if (loaded && introWaitRef.current && Date.now() - introWaitRef.current.at >= REFRESH_MS - 1000) {
        // Заставка за целый период опроса так и не дождалась показа — не держим её:
        // эти алерты сразу встают на орбиту и в ленту. (Проверка по времени, а не «любой следующий
        // ответ»: в StrictMode при разработке первый запрос уходит дважды и второй ответ приходит сразу.)
        introWaitRef.current.items.forEach(a => pendingRef.current.delete(a.id));
        introWaitRef.current = null;
        introPlayed = true;
      }
      [...fly, ...intro].forEach(a => pendingRef.current.add(a.id));
      for (const id of pendingRef.current) if (!alerts.some(a => a.id === id)) pendingRef.current.delete(id);
      setPending(new Set(pendingRef.current));
      scene?.syncOrbit(alerts, pendingRef.current, loaded);
      if (fly.length) scene?.enqueue(fly.map(a => ({ alert: a, texts: cardTexts(a) })));
      if (intro.length) playIntro();
      prevMaxRef.current = Math.max(prevMaxRef.current ?? 0, ...alerts.map(a => a.id));
      writeLastSeen(seenKey, prevMaxRef.current);
      setItems(alerts);
      setCount1h(typeof json.count_1h === 'number' ? json.count_1h : null);
      setState('ok');
      errorDelay.current = ERROR_MIN_MS;
      return REFRESH_MS;
    } catch {
      if (endedRef.current) return null;
      setState('error');
      const delay = errorDelay.current;
      errorDelay.current = Math.min(delay * 2, ERROR_MAX_MS);
      return delay;
    } finally {
      clearTimeout(timer);
    }
  }, [cardTexts, seenKey, reduced, showSince, playIntro]);

  // Опрос: следующий запрос — только после ответа на предыдущий, поэтому запросы не накладываются
  useEffect(() => {
    if (!visible || sessionEnded) return;
    let stopped = false, timer = 0, busy = false;
    const tick = async () => {
      clearTimeout(timer);
      if (stopped || busy || document.hidden) return;
      busy = true;
      const next = await load();
      busy = false;
      if (!stopped && next != null) timer = setTimeout(tick, next);
    };
    const onShow = () => { if (!document.hidden) tick(); };
    tick();
    document.addEventListener('visibilitychange', onShow);
    return () => { stopped = true; clearTimeout(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [visible, sessionEnded, load]);

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const openInJournal = async (id) => {
    if (!onOpenAlert) return;
    setOpening(true);
    setOpenError(false);
    const ok = await onOpenAlert(id);
    setOpening(false);
    if (!ok) setOpenError(true);
  };

  const feed = (items || []).filter(a => !pending.has(a.id));
  const sel = selected && (items || []).find(a => a.id === selected.id);
  // Панель закрывается быстрее, чем открывается: 140 мс держим прежний алерт с анимацией ухода
  const [lastSel, setLastSel] = useState(null);
  useEffect(() => {
    if (sel) { setLastSel({ a: sel, at: selected }); return undefined; }
    if (!lastSel || reduced) { setLastSel(null); return undefined; }
    const timer = setTimeout(() => setLastSel(null), 140);
    return () => clearTimeout(timer);
  }, [sel, selected]);
  const closing = !sel && !!lastSel;
  const shownSel = sel || lastSel?.a;
  const panelAt = (sel ? selected : lastSel?.at) || {};
  const live = state === 'ok' && !paused;
  const status = paused ? t('AlertStream.paused')
    : state === 'error' ? t('AlertStream.state_error')
    : state === 'nodata' ? t('AlertStream.state_nodata')
    : state === 'ended' ? t('AlertStream.state_ended')
    : state === 'loading' ? t('AlertStream.loading')
    : t('AlertStream.live');

  return (
    <section ref={rootRef} className="as-card bg-white border border-gray-200 p-6 md:p-8 rounded-[3rem] shadow-xl flex flex-col">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-red-50 text-red-500"><Radio size={16} /></div>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('AlertStream.title')}</p>
          <p className="flex items-center gap-2 text-xs text-gray-400 italic mt-0.5">
            <span className={`as-live ${live ? '' : 'as-live-off'}`} aria-hidden="true" />
            <span>{status}</span>
            {burstN > 0 && (
              <span className="as-burst not-italic" role="status">{t('AlertStream.burst', { n: burstN })}</span>
            )}
            {since && (
              <span className="as-burst not-italic" role="status">
                {t(since.many ? 'AlertStream.since_visit_many' : 'AlertStream.since_visit', { n: since.n })}
              </span>
            )}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setPaused(p => !p)}
            className="press flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold">
            {paused ? <Play size={12} /> : <Pause size={12} />}
            {paused ? t('AlertStream.resume') : t('AlertStream.pause')}
          </button>
          <button type="button" onClick={toggleFullscreen} title={t('AlertStream.fullscreen')} aria-label={t('AlertStream.fullscreen')}
            className="press p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600">
            <Maximize2 size={14} />
          </button>
        </div>
      </div>

      <div className="as-body flex flex-col md:flex-row gap-6 md:h-[420px]">
        {/* Сцена */}
        <div ref={stageRef} className="relative flex-1 min-w-0 h-[380px] md:h-full rounded-[2rem] border border-[#1e293b] bg-[#0b1022] overflow-hidden">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label={t('AlertStream.scene_label')} />
          <div ref={hubRef} className="as-hub">
            <div className="as-hub-circle" aria-hidden="true">
              <b>SOC</b><i className="as-hub-mark" /><small>Portal</small>
            </div>
          </div>
          {shownSel && (
            <div key={shownSel.id} className={`as-panel ${closing ? 'as-panel-out' : ''}`} role="dialog" aria-label={shownSel.title}
              style={{ left: panelAt.left, top: panelAt.top, transformOrigin: `${panelAt.ox}px ${panelAt.oy}px` }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] font-black px-2 py-0.5 rounded-[0.375rem]"
                  style={{ color: sevColor(shownSel.severity), background: `${sevColor(shownSel.severity)}26` }}>
                  {sevLabel(shownSel.severity).toUpperCase()}
                </span>
                <button type="button" onClick={() => sceneRef.current?.select(null)} aria-label={t('AlertStream.close')}
                  className="press -mt-1 -mr-1 p-1 rounded-[0.5rem] text-slate-400 hover:text-white hover:bg-white/10">
                  <X size={14} />
                </button>
              </div>
              <div className="as-stagger">
                <div className="text-sm font-bold text-slate-50 leading-snug line-clamp-3">{shownSel.title || '—'}</div>
                <div className="text-[11px] text-slate-400 mt-1 truncate">{shownSel.source || '—'} · <span className="font-mono">{time(shownSel.ts)}</span></div>
              </div>
              <button type="button" onClick={() => openInJournal(shownSel.id)} disabled={opening || closing}
                className="press mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-[0.75rem] bg-red-600 hover:bg-red-500 text-white text-xs font-bold disabled:opacity-60">
                <ExternalLink size={12} /> {opening ? t('AlertStream.opening') : t('AlertStream.open_in_journal')}
              </button>
              {openError && <p className="text-[11px] text-red-300 mt-2">{t('AlertStream.open_error')}</p>}
            </div>
          )}
          <div ref={labelsRef} />
          <div className="absolute left-6 right-6 bottom-5 flex flex-wrap items-center justify-between gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400 pointer-events-none">
            <div className="flex flex-wrap gap-4">
              <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-slate-300" />{t('AlertStream.legend_activity')}: <b className="text-slate-100">{activity ?? '—'}</b></span>
              {['Low', 'Medium', 'High', 'Critical'].map(s => (
                <span key={s} className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full" style={{ background: SEVERITY_COLORS[s] }} />{sevLabel(s)}</span>
              ))}
            </div>
            <div>{t('AlertStream.total_24h')}: <b className="text-slate-100">{total24h ?? '—'}</b></div>
          </div>
          <div className="sr-only" aria-live="polite">{announce}</div>
        </div>

        {/* Лента последних алертов */}
        <aside className="md:w-[320px] shrink-0 flex flex-col min-h-0">
          <div className="pb-3 flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('AlertStream.feed_title')}</p>
            {feed.length > 0 && <span className="text-[10px] font-bold text-gray-400">{t('AlertStream.feed_count', { n: feed.length })}</span>}
          </div>
          {feed.length > 0 ? (
            <ul className="flex-1 overflow-y-auto pr-1 space-y-2 max-h-[300px] md:max-h-none">
              {feed.map(a => {
                const isSel = selected?.id === a.id;
                return (
                  <li key={a.id} className={arrived.has(a.id) ? 'as-in' : ''}>
                    <button type="button"
                      onClick={() => sceneRef.current?.select(isSel ? null : a.id)}
                      onMouseEnter={() => sceneRef.current?.highlight(a.id)}
                      onMouseLeave={() => sceneRef.current?.highlight(null)}
                      onFocus={() => sceneRef.current?.highlight(a.id)}
                      onBlur={() => sceneRef.current?.highlight(null)}
                      aria-pressed={isSel}
                      className={`press-wide w-full text-left rounded-[1rem] px-4 py-3 flex gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600
                        ${isSel ? 'bg-white ring-1 ring-gray-300 shadow-sm' : arrived.has(a.id) && BIG.has(a.severity) ? 'bg-red-50' : 'bg-gray-50 hover:bg-gray-100'}`}>
                      <span className="w-1 rounded-full shrink-0" style={{ background: sevColor(a.severity) }} />
                      <span className="min-w-0 flex-1">
                        <span className="flex justify-between gap-2 text-[10px] font-black uppercase tracking-widest">
                          <span style={{ color: sevColor(a.severity) }}>{sevLabel(a.severity)}</span>
                          <span className="text-gray-400 font-mono tabular-nums">{time(a.ts)}</span>
                        </span>
                        <span className="block text-xs font-bold text-gray-900 truncate mt-0.5" title={a.title}>{a.title || '—'}</span>
                        <span className="block text-[11px] text-gray-400 italic truncate" title={a.source}>{a.source || '—'}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center rounded-[1rem] bg-gray-50 px-4 py-10">
              <p className="text-sm font-bold text-gray-500">
                {state === 'loading' ? t('AlertStream.loading')
                  : state === 'error' ? t('AlertStream.error_title')
                  : state === 'nodata' ? t('AlertStream.nodata_title')
                  : state === 'ended' ? t('AlertStream.state_ended')
                  : t('AlertStream.empty_title')}
              </p>
              <p className="text-xs text-gray-400 italic mt-1">
                {state === 'error' ? t('AlertStream.error_hint')
                  : state === 'nodata' ? t('AlertStream.nodata_hint')
                  : state === 'ok' ? t('AlertStream.empty_hint') : ''}
              </p>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
